/* eslint-disable class-methods-use-this */
/* eslint-disable no-underscore-dangle */
import type {
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  EditorConfig,
  ElementFormatType,
  LexicalEditor,
  LexicalNode,
  NodeKey,
  Spread,
} from 'lexical';

import { BlockWithAlignableContents } from '@lexical/react/LexicalBlockWithAlignableContents';
import {
  DecoratorBlockNode,
  SerializedDecoratorBlockNode,
} from '@lexical/react/LexicalDecoratorBlockNode';
import { Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import pdfLogo from './pdf.svg';
import docxLogo from './docx.svg';
import pptxLogo from './pptx.svg';
import xlsxLogo from './xlsx.svg';
import StyledBox from './styles';

type DocumentLinkComponentProps = Readonly<{
  className: Readonly<{
    base: string;
    focus: string;
  }>;
  format: ElementFormatType | null;
  nodeKey: NodeKey;
  url: string;
  name: string;
}>;

function convertDocumentLinkElement(
  domNode: HTMLDivElement,
): DOMConversionOutput | null {
  const url = domNode.getAttribute('data-lexical-pdf-link-url');
  const name = domNode.getAttribute('data-lexical-pdf-link-name');
  if (url && name) {
    const node = $createDocumentLinkNode(url, name);
    return { node };
  }
  return null;
}
function DocumentLinkComponent({
  name,
  url,
  className,
  format,
  nodeKey,
}: DocumentLinkComponentProps) {
  React.useEffect(() => {
    const script = document.createElement('script');
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);
  const [isHovered, setIsHovered] = React.useState(false);
  const { t } = useTranslation();
  const fileType = url.split('.').pop() || '';
  const lowerCaseFileType = fileType.toLowerCase();
  const map = {
    pdf: { src: pdfLogo, alt: 'Download PDF' },
    docx: { src: docxLogo, alt: 'Download Word Document' },
    pptx: { src: pptxLogo, alt: 'Download PowerPoint Presentation' },
    xlsx: { src: xlsxLogo, alt: 'Download Excel File' },
  };
  return (
    <BlockWithAlignableContents
      className={className}
      format={format}
      nodeKey={nodeKey}
    >
      <StyledBox>
        <a href={url} target="_blank" rel="noreferrer" onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
          <img src={map[lowerCaseFileType].src} alt={map[lowerCaseFileType].alt} />
          <div>
            <Typography variant="h2">
              {name}
            </Typography>
            <Typography variant="caption">
              {isHovered ? t('decisionTree.tree.editor.document-link') : fileType}
            </Typography>
          </div>
        </a>
      </StyledBox>
    </BlockWithAlignableContents>
  );
}

export type SerializedDocumentLinkNode = Spread<
  {
    url: string;
    name: string;
    type: 'pdf-link';
    version: 1;
  },
  SerializedDecoratorBlockNode
>;

export class DocumentLinkNode extends DecoratorBlockNode {
  __url: string;

  __name: string;

  static getType(): string {
    return 'pdf-link';
  }

  static clone(node: DocumentLinkNode): DocumentLinkNode {
    return new DocumentLinkNode(node.__url, node.__name, node.__format, node.__key);
  }

  static importJSON(serializedNode: SerializedDocumentLinkNode): DocumentLinkNode {
    const node = $createDocumentLinkNode(serializedNode.url, serializedNode.name);
    node.setFormat(serializedNode.format);
    return node;
  }

  exportJSON(): SerializedDocumentLinkNode {
    return {
      ...super.exportJSON(),
      url: this.getUrl(),
      name: this.getName(),
      type: 'pdf-link',
      version: 1,
    };
  }

  static importDOM(): DOMConversionMap<HTMLDivElement> | null {
    return {
      div: (domNode: HTMLDivElement) => {
        if (!domNode.hasAttribute('data-lexical-pdf-link-url') || !domNode.hasAttribute('data-lexical-pdf-link-name')) {
          return null;
        }
        return {
          conversion: convertDocumentLinkElement,
          priority: 2,
        };
      },
    };
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement('div');
    element.setAttribute('data-lexical-pdf-link-url', this.__url);
    element.setAttribute('data-lexical-pdf-link-name', this.__name);
    return { element };
  }

  constructor(url: string, name: string, format?: ElementFormatType, key?: NodeKey) {
    super(format, key);
    this.__url = url;
    this.__name = name;
  }

  getUrl(): string {
    return this.__url;
  }

  getName(): string {
    return this.__name;
  }

  decorate(editor: LexicalEditor, config: EditorConfig): JSX.Element {
    const embedBlockTheme = config.theme.embedBlock || {};
    const className = {
      base: embedBlockTheme.base || '',
      focus: embedBlockTheme.focus || '',
    };
    return (
      <DocumentLinkComponent
        className={className}
        format={this.__format}
        nodeKey={this.getKey()}
        url={this.__url}
        name={this.__name}
      />
    );
  }

  isTopLevel(): true {
    return true;
  }
}

export function $createDocumentLinkNode(url: string, displayText: string): DocumentLinkNode {
  return new DocumentLinkNode(url, displayText);
}

export function $isDocumentLinkNode(
  node: DocumentLinkNode | LexicalNode | null | undefined,
): node is DocumentLinkNode {
  return node instanceof DocumentLinkNode;
}
